import { type QueryFunctionContext } from '@tanstack/react-query'
import { apiPost } from '@/features/shared/utils/dataFetching'
import {
  type SearchCouponsResponseLegacy,
  type SearchCouponsLegacy,
  type CategoryFacet,
  type GlobalSearchResponse,
  type MoreLikeThisSearchResponse,
  type SearchCoupons,
  type SearchCouponsResponse,
  type SearchResponse,
} from '@/features/shop/services/Search/types'
import {
  PURCHASE_HISTORY_PAGE_SIZE,
  SEARCH_FACETS,
  SEARCH_FILTER_TYPE,
} from '@/features/shared/constants/global'
import {
  type GlobalSearchQueryKey,
  type PurchaseHistoryQueryKey,
  type SearchQueryKey,
  type BuyAgainCategoriesQueryKey,
  type CouponsSearchQueryKey,
  type CouponsSearchQueryKeyLegacy,
  type MoreLikeThisSearchQueryKey,
} from '@/features/shop/services/Search/queries'
import { getSectionId } from '@/features/shared/utils/getSearchSection'
import { normalizeCoupon } from '@/features/shop/utils/normalizeCoupon'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import {
  calculateTotalPages,
  normalizeCouponFacets,
  normalizeSearchResults,
  getAllowedFacetsForParams,
  normalizeFacets,
} from '@/features/shop/services/Search/utils'
import { without } from '@/features/shared/utils/without'

export const fetchGlobalSearch = async (context: QueryFunctionContext<GlobalSearchQueryKey>) => {
  const [, { query, contexts, userId, category_ids }] = context.queryKey
  const data = await apiPost<GlobalSearchResponse>({
    config: {
      url: '/search/v4/multi-retailer/search',
      data: {
        query,
        size_per_store: 12,
        contexts,
        user_id: userId,
        ...(category_ids?.length && {
          category: {
            ids: category_ids,
            operator: 'or',
          },
        }),
        allow_redirect: true,
      },
    },
    context,
    fetcherName: 'fetchGlobalSearch',
  })

  return {
    ...data,
    retailer_hits: data.retailer_hits?.map((retailer) => {
      return {
        ...retailer,
        items: retailer.items.map((product) => normalizeProduct(product)),
      }
    }),
  }
}

export const fetchSearchProducts = async (
  context: QueryFunctionContext<SearchQueryKey, number>,
) => {
  const { pageParam, queryKey } = context
  const [, { storeParams, params, pageSize }] = queryKey
  const searchResponse = await apiPost<SearchResponse>({
    config: {
      url: '/search/v4/search/',
      params: {
        page_size: pageSize,
      },
      data: {
        ...storeParams,
        ...params,
        facets: getAllowedFacetsForParams(params),
        section_id: getSectionId(!params.query),
        include_endcaps: true,
        page: pageParam || 1,
        featured: true,
        allow_redirect: true,
        hits_per_page: pageSize,
        ...('category_ids' in params && {
          category_ids_query_operator: 'or',
        }),
      },
    },
    context,
    fetcherName: 'fetchSearchProducts',
  })
  return normalizeSearchResults(searchResponse, params)
}

export const fetchBuyAgainCategories = async (
  context: QueryFunctionContext<BuyAgainCategoriesQueryKey>,
) => {
  const [, storeParams] = context.queryKey
  const response = await apiPost<SearchResponse>({
    config: {
      url: '/search/v4/purchase-history/search',
      data: {
        ...storeParams,
        facets: [SEARCH_FACETS.ACTIVE_DEALS, SEARCH_FACETS.H1_CATEGORIES],
        hits_per_page: 1,
        hidden: false,
      },
    },
    context,
    fetcherName: 'fetchBuyAgainCategories',
  })
  const data = normalizeSearchResults(response)
  const items = data.facets
    .filter(
      (f): f is CategoryFacet =>
        f.facet === SEARCH_FILTER_TYPE.CATEGORY || f.facet === SEARCH_FILTER_TYPE.ACTIVE_DEALS,
    )
    .flatMap((x) => x.items)

  return items
}

export const fetchSearchCoupons = async (
  context: QueryFunctionContext<CouponsSearchQueryKey, string>,
): Promise<SearchCoupons> => {
  const { pageParam, queryKey } = context
  const [, { categoryId, ...storeParams }] = queryKey

  const response = await apiPost<SearchCouponsResponse>({
    config: {
      url: 'search/v1/products/coupons',
      data: {
        location: {
          store_id: String(storeParams.store_id ?? ''),
          store_location_id: String(storeParams.store_location_id ?? ''),
          metro_id: String(storeParams.metro_id ?? ''),
        },
        size: 48,
        ...(pageParam && { after: pageParam }),
        facet_values: {
          categories: {
            ...(categoryId && {
              filter_to: [categoryId],
            }),
          },
        },
      },
    },
    context,
    fetcherName: 'fetchSearchCoupons',
  })

  return {
    ...response,
    result: (response.result ?? []).map(normalizeCoupon),
    facets: normalizeCouponFacets(response.facets),
  }
}

export const fetchSearchCouponsLegacy = async (
  context: QueryFunctionContext<
    CouponsSearchQueryKeyLegacy,
    { coupon_after_key: string; page: number }
  >,
): Promise<SearchCouponsLegacy> => {
  const { pageParam, queryKey } = context
  const [, { categoryId, ...storeParams }] = queryKey
  const { coupon_after_key, page } = pageParam
  const response = await apiPost<SearchCouponsResponseLegacy>({
    config: {
      params: storeParams,
      url: 'search/v3/coupons/search',
      data: {
        ...storeParams,
        ...(coupon_after_key && { coupon_after_key }),
        ...(categoryId && { category_id: Number(categoryId) }),
        facets: [SEARCH_FACETS.CATEGORIES, SEARCH_FACETS.H1_CATEGORIES],
        coupon_size: 48,
        page: page || 1,
      },
    },
    context,
    fetcherName: 'fetchSearchCouponsLegacy',
  })
  return {
    ...response,
    facets: normalizeFacets(response.facets),
    coupons: response.coupons.map(normalizeCoupon),
  }
}

export const fetchPurchaseHistory = async (
  context: QueryFunctionContext<PurchaseHistoryQueryKey, number>,
) => {
  const { pageParam, queryKey } = context
  const [, { storeParams, params }] = queryKey

  // We cant reuse the query param for buy again search since that will affect the main search bar as well
  // Use ?buy_again_query instead and map it to the query param in the /search/v4/purchase-history/ call
  const mappedParams = {
    ...params,
    query: params.buy_again_query,
  }

  const response = await apiPost<SearchResponse>({
    config: {
      url: '/search/v4/purchase-history/search',
      data: {
        ...storeParams,
        ...mappedParams,
        facets: without(Object.values(SEARCH_FACETS), SEARCH_FACETS.BRAND, SEARCH_FACETS.FLAVOR),
        page: pageParam || 1,
        hits_per_page: PURCHASE_HISTORY_PAGE_SIZE,
        hidden: false,
        include_oos: true,
        include_low_stock: true,
      },
    },
    context,
    fetcherName: 'fetchPurchaseHistory',
  })
  const { total_hits } = response
  const hits = response.hits?.map((p) => ({ ...p, bought: true })) || []
  const searchResults = {
    ...normalizeSearchResults({ ...response, hits }, params),
    totalPages: calculateTotalPages(total_hits),
  }
  return searchResults
}

export const fetchMoreLikeThisItemsSearch = async (
  context: QueryFunctionContext<MoreLikeThisSearchQueryKey>,
) => {
  const [, { storeParams, product_id, size }] = context.queryKey
  const data = await apiPost<MoreLikeThisSearchResponse>({
    config: {
      url: '/search/v1/more-like-this',
      data: { ...storeParams, product_id, size },
      onErrorLog: { product_id },
    },
    context,
    fetcherName: 'fetchMoreLikeThisItemsSearch',
  })
  return data.hits.map((product) => normalizeProduct(product))
}
